import React, { Component, ReactNode } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw } from 'lucide-react';

interface DebugErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface DebugErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * Error boundary specifically for debug components.
 * Catches errors in debug UI and provides fallback behavior.
 */
export class DebugErrorBoundary extends Component<DebugErrorBoundaryProps, DebugErrorBoundaryState> {
  constructor(props: DebugErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<DebugErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Debug component error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log debug system error for monitoring
    this.logDebugError(error, errorInfo);
  }

  private logDebugError(error: Error, errorInfo: React.ErrorInfo) {
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
    };

    // Log to console for development
    console.group('🐛 Debug System Error');
    console.error('Error:', error);
    console.error('Component Stack:', errorInfo.componentStack);
    console.error('Error Data:', errorData);
    console.groupEnd();

    // Store error in localStorage for debugging
    try {
      const existingErrors = JSON.parse(localStorage.getItem('debug-system-errors') || '[]');
      existingErrors.push(errorData);
      
      // Keep only last 10 errors
      const recentErrors = existingErrors.slice(-10);
      localStorage.setItem('debug-system-errors', JSON.stringify(recentErrors));
    } catch (storageError) {
      console.warn('Failed to store debug error:', storageError);
    }
  }

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  private handleDisableDebug = () => {
    // Disable debug mode to prevent further errors
    try {
      const debugSettings = JSON.parse(localStorage.getItem('ai-character-studio-debug-settings') || '{}');
      debugSettings.isEnabled = false;
      localStorage.setItem('ai-character-studio-debug-settings', JSON.stringify(debugSettings));
      
      // Reload the page to reset debug state
      window.location.reload();
    } catch (error) {
      console.error('Failed to disable debug mode:', error);
    }
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Alert variant="destructive" className="m-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="space-y-3">
            <div>
              <div className="font-medium">Debug System Error</div>
              <div className="text-sm mt-1">
                The debug interface encountered an error and has been disabled to prevent further issues.
              </div>
            </div>
            
            {this.state.error && (
              <div className="p-2 rounded border text-xs font-mono bg-red-50 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-100 dark:border-red-800">
                {this.state.error.message}
              </div>
            )}
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={this.handleRetry}
                className="flex items-center gap-1"
              >
                <RefreshCw className="h-3 w-3" />
                Retry
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={this.handleDisableDebug}
              >
                Disable Debug Mode
              </Button>
            </div>
            
            <div className="text-xs text-muted-foreground">
              AI requests will continue to work normally. You can re-enable debug mode later.
            </div>
          </AlertDescription>
        </Alert>
      );
    }

    return this.props.children;
  }
}
